import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Img04 from 'ui/images/top/topService_img04@2x.png';
import HintIcon from 'ui/images/icons/hint.png';
import { TipsForQuestionnaires } from './TipsForQuestionnaires';

type AboutServiceProps = {
    tabs?: string[];
    descriptions: { [key: string]: string[] };
    showTipsForQuestionnaires?: boolean;
};

const Container = styled.div`
    width: 100%;
    margin: 0 auto 20px auto;
`;

const TabList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
`;

const TabButton = styled.button<{ active: boolean }>`
    padding: 8px 16px;
    background-color: var(--main-color-blue);
    ${({ active }) => (!active ? 'opacity: 0.7;' : '')}
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        margin: -8px -9px -5px -2px;
        transform: rotate(15deg);
    }
`;

const ContentBox = styled.div<{ isOpen: boolean }>`
    background-color: var(--soft-blue);
    padding: 8px;
    border-radius: 10px;
    width: 100%;
    height: ${({ isOpen }) => (isOpen ? 'auto' : '0')};
    overflow: hidden;
    transition: height 0.3s ease-in-out, opacity 0.3s ease-in-out;
    opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
`;

const ToggleButton = styled.button`
    font-size: 14px;
    color: #2a7be4;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    margin-top: 8px;
`;

const FlexContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    font-weight: 600;

    img {
        width: 100px;
        margin: 0 auto;
    }
`;

const TextContainer = styled.div`
    flex: 1;
    text-align: left;
    padding-top: 5px;

    p {
        font-size: 14px;
        margin: 0;
        margin-bottom: 10px;
    }
`;

export const AboutService: React.FC<AboutServiceProps> = ({
    tabs,
    descriptions,
    showTipsForQuestionnaires = false,
}) => {
    const [enableTipsForQuestionnaires, setEnableTipsForQuestionnaires] = useState(
        typeof window !== 'undefined' ? window.innerWidth > 768 && showTipsForQuestionnaires : true,
    );

    useEffect(() => {
        const handleResize = () => {
            setEnableTipsForQuestionnaires(window.innerWidth > 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const initialKey = enableTipsForQuestionnaires
        ? 'tipsForQuestionnaires'
        : tabs?.[0] ?? Object.keys(descriptions)[0];
    const [activeTab, setActiveTab] = useState(initialKey);
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        if (!enableTipsForQuestionnaires && activeTab === 'tipsForQuestionnaires') {
            setActiveTab(tabs?.[0] ?? Object.keys(descriptions)[0]);
        }
        if (enableTipsForQuestionnaires && activeTab == null) {
            setActiveTab('tipsForQuestionnaires');
        }
    }, [enableTipsForQuestionnaires, tabs, descriptions, activeTab]);

    return (
        <Container>
            {tabs && tabs.length > 0 && (
                <TabList>
                    {showTipsForQuestionnaires && enableTipsForQuestionnaires && (
                        <TabButton
                            active={activeTab === 'tipsForQuestionnaires'}
                            onClick={() => setActiveTab('tipsForQuestionnaires')}
                        >
                            面談前に確認すべきポイント
                            <img src={HintIcon} alt="電球アイコン" style={{ width: '35px' }} />
                        </TabButton>
                    )}
                    {tabs.map((tab) => (
                        <TabButton key={tab} active={tab === activeTab} onClick={() => setActiveTab(tab)}>
                            {tab}
                        </TabButton>
                    ))}
                </TabList>
            )}

            {activeTab === 'tipsForQuestionnaires' && enableTipsForQuestionnaires ? (
                <TipsForQuestionnaires />
            ) : (
                <ContentBox isOpen={isOpen}>
                    <FlexContainer>
                        <img src={Img04} alt="説明用イラスト" />
                        <TextContainer>
                            {(descriptions[activeTab] ?? []).map((line, idx) => (
                                <p key={idx}>{line}</p>
                            ))}
                        </TextContainer>
                    </FlexContainer>
                    <ToggleButton onClick={() => setIsOpen(false)}>説明を折りたたむ</ToggleButton>
                </ContentBox>
            )}

            {!isOpen && activeTab !== 'tipsForQuestionnaires' && (
                <ToggleButton onClick={() => setIsOpen(true)}>説明を表示</ToggleButton>
            )}
        </Container>
    );
};
